import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { GripVertical, FileText, Info, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

// Sortable Item Component
function SortableSheetItem({ sheet, toggleSheet, removeSheet, t }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sheet.sheet_number });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`p-4 transition-all border border-slate-200 dark:border-slate-700 ${
        sheet.included
          ? 'bg-white dark:bg-slate-800 shadow-sm'
          : 'bg-slate-50 dark:bg-slate-900/50 opacity-60'
      } ${isDragging ? 'shadow-lg ring-2 ring-blue-500 opacity-50' : ''}`}
      data-testid={`sheet-${sheet.sheet_number}`}
    >
      <div className="flex items-center space-x-4">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5 text-slate-400" />
        </div>

        <Checkbox
          checked={sheet.included}
          onCheckedChange={() => toggleSheet(sheet.sheet_number)}
          data-testid={`checkbox-${sheet.sheet_number}`}
        />

        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <span className="font-mono text-sm font-semibold text-blue-600">
              {sheet.sheet_number}
            </span>
            <span className="text-slate-900 dark:text-white font-medium">
              {sheet.title}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {sheet.sheet_type.replace('_', ' ').toUpperCase()}
          </p>
        </div>

        {sheet.content_blocks && sheet.content_blocks.length > 0 && (
          <span className="text-xs text-slate-500">
            {sheet.content_blocks.length} {t('blocks')}
          </span>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => removeSheet(sheet.sheet_number)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`remove-${sheet.sheet_number}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

const SheetIndexStep = ({ data, onChange }) => {
  const { t } = useLanguage();
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [newSheet, setNewSheet] = useState({
    sheet_number: '',
    title: '',
    sheet_type: 'general',
    included: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const toggleSheet = (sheetNumber) => {
    const updatedSheets = data.sheets.map(sheet =>
      sheet.sheet_number === sheetNumber
        ? { ...sheet, included: !sheet.included }
        : sheet
    );
    onChange({ ...data, sheets: updatedSheets });
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = data.sheets.findIndex((s) => s.sheet_number === active.id);
      const newIndex = data.sheets.findIndex((s) => s.sheet_number === over.id);

      const reorderedSheets = arrayMove(data.sheets, oldIndex, newIndex).map((sheet, index) => ({
        ...sheet,
        order: index,
      }));

      onChange({ ...data, sheets: reorderedSheets });
    }
  };
  
  const addNewSheet = () => {
    if (!newSheet.sheet_number || !newSheet.title) {
      toast.error('Please fill in sheet number and title');
      return;
    }
    
    // Check if sheet number already exists
    if (data.sheets.some(s => s.sheet_number === newSheet.sheet_number)) {
      toast.error('Sheet number already exists');
      return;
    }
    
    const sheetToAdd = {
      ...newSheet,
      order: data.sheets.length,
      content_blocks: [],
    };
    
    onChange({ ...data, sheets: [...data.sheets, sheetToAdd] });
    setNewSheet({ sheet_number: '', title: '', sheet_type: 'general', included: true });
    setShowAddSheet(false);
    toast.success('Sheet added successfully');
  };
  
  const removeSheet = (sheetNumber) => {
    const updatedSheets = data.sheets.filter(s => s.sheet_number !== sheetNumber);
    onChange({ ...data, sheets: updatedSheets });
    toast.success('Sheet removed');
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {t('sheetIndex')}
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          {t('selectSheetsToInclude')}
        </p>
      </div>
      
      {/* Explanation Card */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-slate-200 dark:border-slate-700">
        <div className="flex items-start space-x-3">
          <Info className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
              {t('intelligentPagination')}
            </h4>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {t('paginationDescription')}
            </p>
          </div>
        </div>
      </Card>
      
      {/* Add Sheet Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setShowAddSheet(!showAddSheet)}
          data-testid="add-sheet-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('addSheet') || 'Add Sheet'}
        </Button>
      </div>
      
      {/* Add Sheet Form */}
      {showAddSheet && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('addNewSheet') || 'Add New Sheet'}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddSheet(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sheet_number">{t('sheetNumber') || 'Sheet Number'}</Label>
              <Input
                id="sheet_number"
                value={newSheet.sheet_number}
                onChange={(e) => setNewSheet({ ...newSheet, sheet_number: e.target.value })}
                placeholder="e.g., A7.00"
              />
            </div>
            <div>
              <Label htmlFor="sheet_title">{t('sheetTitle') || 'Sheet Title'}</Label>
              <Input
                id="sheet_title"
                value={newSheet.title}
                onChange={(e) => setNewSheet({ ...newSheet, title: e.target.value })}
                placeholder="e.g., DETAILS"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="outline" onClick={() => setShowAddSheet(false)}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button onClick={addNewSheet} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
              {t('add') || 'Add'}
            </Button>
          </div>
        </Card>
      )}
      
      {data.sheets && data.sheets.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={data.sheets.map((s) => s.sheet_number)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {data.sheets.map((sheet) => (
                <SortableSheetItem
                  key={sheet.sheet_number}
                  sheet={sheet}
                  toggleSheet={toggleSheet}
                  removeSheet={removeSheet}
                  t={t}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card className="p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
          <FileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400 font-medium">
            {t('selectProjectTypeToSeeSheets')}
          </p>
        </Card>
      )}
      
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 rounded-lg border border-slate-200">
        <div>
          <p className="text-sm font-medium text-slate-900 dark:text-white">
            {t('totalSheetsSelected')}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {t('theseWillBeIncluded')}
          </p>
        </div>
        <div className="text-3xl font-bold text-blue-600">
          {data.sheets?.filter(s => s.included).length || 0}
        </div>
      </div>
    </div>
  );
};

export default SheetIndexStep;
